import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { money, requestError, requestId, timestampDay } from './workshopFinanceModel';
import { smartPos } from '../../services/smartPos';

const today = () => timestampDay(new Date().toISOString());
const isPayable = document => document?.kind === 'expense'
  && document?.payment_method === 'credit'
  && document?.supplier?.id
  && Number(document?.outstanding_amount) > 0;

export default function SmartPosSupplierSettlement({ onClose, onSaved }) {
  const [documents, setDocuments] = useState([]);
  const [documentId, setDocumentId] = useState('');
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [description, setDescription] = useState('');
  const [canSettle, setCanSettle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const payload = useRef(null);
  const submitting = useRef(false);

  useEffect(() => {
    let active = true;
    smartPos.documents({ kind: 'expense', outstanding_only: true, limit: 100 }).then(({ data }) => {
      if (!data?.success || !Array.isArray(data.items)) throw new Error('Invalid POS payable response');
      const payables = data.items.filter(isPayable);
      if (!active) return;
      setDocuments(payables);
      setCanSettle(data.permissions?.can_settle === true);
      setDocumentId(current => payables.some(document => document.id === current) ? current : (payables[0]?.id || ''));
    }).catch(() => {
      if (active) {
        setDocuments([]);
        setError('تعذر تحميل مستحقات المورد من نقطة البيع. لم تُعرض بيانات بديلة.');
      }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const document = useMemo(() => documents.find(item => item.id === documentId) || null, [documents, documentId]);
  const clearRequest = () => { payload.current = null; setError(''); };
  const changeDocument = value => {
    clearRequest();
    setDocumentId(value);
    setAmount('');
    setDescription('');
  };
  const submit = async event => {
    event.preventDefault();
    if (!document || !canSettle || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      if (!payload.current) payload.current = {
        request_id: requestId(), amount, payment_method: method, entry_date: date,
        description: description.trim(),
      };
      const { data } = await smartPos.settle(document.id, payload.current);
      if (!data?.success || !data?.document?.id) {
        setUncertain(true);
        throw new Error('Unconfirmed supplier settlement');
      }
      onSaved(data.document);
    } catch (failure) {
      if (payload.current && (!failure.response || failure.response.status >= 500)) setUncertain(true);
      setError(requestError(failure));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return <Dialog open onOpenChange={open => { if (!open && !busy && !uncertain) onClose(); }}>
    <DialogContent className="wms-movement-dialog" dir="rtl" onEscapeKeyDown={event => { if (busy || uncertain) event.preventDefault(); }} onInteractOutside={event => { if (busy || uncertain) event.preventDefault(); }}>
      <DialogHeader>
        <DialogTitle>تسوية مستحق مورد</DialogTitle>
        <DialogDescription>تسدد مستحقًا مستقلًا أُنشئ من نقطة البيع. شراء المركبة أو قطعها يبقى في ملف الزيارة ومصدره المخصص.</DialogDescription>
      </DialogHeader>
      {loading ? <p role="status">جارٍ تحميل المستحقات…</p> : <form className="wms-movement-form" onSubmit={submit}>
        <fieldset disabled={busy || uncertain || !canSettle || !documents.length}>
          <div><Label htmlFor="smart-pos-payable">المستحق</Label><select id="smart-pos-payable" required value={documentId} onChange={event => changeDocument(event.target.value)}>
            <option value="">اختر مستحق المورد</option>
            {documents.map(item => <option key={item.id} value={item.id}>{item.supplier?.name || 'مورد'} · {item.number || 'رقم غير مسجل'} · المتبقي {money(item.outstanding_amount)}</option>)}
          </select></div>
          {document ? <div className="smart-pos-settlement-summary" aria-label="ملخص المستحق"><strong>لصالح المورد: {document.supplier?.name}</strong><span>المتبقي: {money(document.outstanding_amount)}</span><small>{document.description || 'وصف غير مسجل'}</small></div> : null}
          <div className="wms-form-grid"><div><Label htmlFor="smart-pos-settlement-amount">مبلغ السداد · ر.س</Label><Input id="smart-pos-settlement-amount" value={amount} type="number" inputMode="decimal" min="0.01" max={document?.outstanding_amount || undefined} step="0.01" required onChange={event => { clearRequest(); setAmount(event.target.value); }} /></div><div><Label htmlFor="smart-pos-settlement-date">التاريخ</Label><Input id="smart-pos-settlement-date" type="date" value={date} max={today()} required onChange={event => { clearRequest(); setDate(event.target.value); }} /></div></div>
          <div><Label htmlFor="smart-pos-settlement-method">طريقة السداد</Label><select id="smart-pos-settlement-method" value={method} onChange={event => { clearRequest(); setMethod(event.target.value); }}><option value="cash">نقدي</option><option value="bank">بنكي</option></select></div>
          <div><Label htmlFor="smart-pos-settlement-description">الوصف</Label><Textarea id="smart-pos-settlement-description" required maxLength={1000} rows={3} value={description} placeholder="مثل: سداد فاتورة المورد" onChange={event => { clearRequest(); setDescription(event.target.value); }} /></div>
        </fieldset>
        {!documents.length && !error ? <p className="wms-finance-state">لا توجد مستحقات مورد مستقلة من نقطة البيع حاليًا.</p> : null}
        {!canSettle && !error ? <p className="wms-finance-notice">صلاحية تسوية المستحقات غير متاحة لهذا المستخدم.</p> : null}
        {error ? <p className="wms-finance-error" role="alert">{error}</p> : null}
        {uncertain ? <p className="wms-finance-notice">نتيجة السداد غير مؤكدة. أعد المحاولة دون تغيير البيانات؛ سيُستخدم المرجع نفسه لمنع التكرار.</p> : null}
        <footer><Button type="button" variant="outline" disabled={busy || uncertain} onClick={onClose}>إلغاء</Button><Button type="submit" disabled={busy || !canSettle || !document || !amount}>{busy ? 'جارٍ السداد…' : uncertain ? 'التحقق وإعادة المحاولة' : 'تأكيد السداد'}</Button></footer>
      </form>}
    </DialogContent>
  </Dialog>;
}
